import React, { useEffect, useState } from "react";
import { getAIUsage, subscribeToAIUsage, AIUsageStats as AIUsageStatsType } from "../aiUsageTracker";
import * as Popover from "@radix-ui/react-popover";

const FREE_TIER_LIMITS = {
  requests: 1500, // per day
  tokens: 1000000, // per minute
};

export const AIUsageStats = () => {
  const [usage, setUsage] = useState<AIUsageStatsType>(getAIUsage());
  
  useEffect(() => {
    return subscribeToAIUsage(setUsage);
  }, []);

  const requestPercentage = Math.min((usage.requests / FREE_TIER_LIMITS.requests) * 100, 100);

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            padding: "0.4rem 0.8rem",
            borderRadius: "9999px",
            background: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            color: "inherit",
            cursor: "pointer",
            fontWeight: 600,
            fontSize: "0.85rem",
            transition: "all 0.2s ease-in-out",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.15)";
            e.currentTarget.style.transform = "translateY(-1px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          <span style={{ fontSize: "1.1rem" }}>⚡</span>
          <span>{usage.requests} <span style={{opacity: 0.6}}>/ {FREE_TIER_LIMITS.requests}</span></span>
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="end"
          sideOffset={8}
          style={{
            zIndex: 99999,
            padding: "1.25rem",
            width: "280px",
            background: "rgba(30, 30, 35, 0.85)",
            backdropFilter: "blur(20px)",
            borderRadius: "16px",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)",
            color: "#fff",
            fontFamily: "Inter, sans-serif",
            animation: "fadeIn 0.2s ease-out",
          }}
        >
          <h3 style={{ margin: "0 0 1rem 0", fontSize: "1rem", fontWeight: 600, display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span style={{ background: "linear-gradient(135deg, #FF6B6B, #845EF7, #339AF0)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              Gemini AI Status
            </span>
          </h3>

          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            
            {/* Requests Progress */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem", fontSize: "0.85rem", color: "rgba(255, 255, 255, 0.8)" }}>
                <span>Today's Requests</span>
                <span style={{ fontWeight: 600 }}>{usage.requests} / {FREE_TIER_LIMITS.requests}</span>
              </div>
              <div style={{ width: "100%", height: "6px", background: "rgba(255, 255, 255, 0.1)", borderRadius: "99px", overflow: "hidden" }}>
                <div style={{ 
                  height: "100%", 
                  width: `${requestPercentage}%`, 
                  background: requestPercentage > 80 ? "#FF6B6B" : "linear-gradient(90deg, #845EF7, #339AF0)",
                  borderRadius: "99px",
                  transition: "width 0.5s cubic-bezier(0.4, 0, 0.2, 1)"
                }} />
              </div>
            </div>

            {/* Tokens Count */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.25rem", fontSize: "0.85rem", color: "rgba(255, 255, 255, 0.8)" }}>
                <span>Tokens Used Today</span>
                <span style={{ fontWeight: 600, color: "#339AF0" }}>{usage.tokens.toLocaleString()}</span>
              </div>
              <p style={{ margin: 0, fontSize: "0.75rem", color: "rgba(255,255,255,0.5)" }}>Free tier allows ~1M tokens per minute.</p>
            </div>

            <div style={{ marginTop: "0.5rem", padding: "0.75rem", background: "rgba(132, 94, 247, 0.1)", borderRadius: "8px", border: "1px solid rgba(132, 94, 247, 0.2)" }}>
              <p style={{ margin: 0, fontSize: "0.75rem", color: "rgba(255, 255, 255, 0.7)", lineHeight: 1.4 }}>
                Keep an eye on your usage to avoid hitting the limits. Counters automatically reset at midnight.
              </p>
            </div>
          </div>
          <Popover.Arrow fill="rgba(30, 30, 35, 0.85)" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
